/**
 * Formats `fr-FR` du classement — une seule source pour les montants et les
 * nombres affichés (podium, rangs, pays). `Intl` fait le travail : espace fine
 * insécable pour les milliers, virgule décimale, symbole après (« 7 327 $US »,
 * « 34,99 € », « 6 755 € »).
 */

export type FormatClassement = "montant" | "nombre";

/** Devise ISO-4217 (`"EUR"`, `"USD"`…). */
export type Devise = string;

/**
 * Montant en unités (pas en centimes). Entier → sans décimales (« 6 755 € »),
 * sinon deux décimales (« 34,99 € »).
 */
export function formatMontant(valeur: number, devise: Devise = "EUR"): string {
  const entier = Number.isInteger(valeur);
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: devise,
    minimumFractionDigits: entier ? 0 : 2,
    maximumFractionDigits: entier ? 0 : 2,
  }).format(valeur);
}

/** Nombre simple (« 1 204 »), sans décimales. */
export function formatNombre(valeur: number): string {
  return new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 }).format(valeur);
}

/** Dispatch selon le `format` reçu en prop. */
export function formatValeur(valeur: number, format: FormatClassement, devise?: Devise): string {
  return format === "montant" ? formatMontant(valeur, devise) : formatNombre(valeur);
}

/** Ce que les composants appellent : le formateur de l'appelant s'il y en a un,
 *  sinon `format` + `devise`. */
export function formatAvec(
  valeur: number,
  opts: { format?: FormatClassement; devise?: Devise; formater?: (valeur: number) => string },
): string {
  if (opts.formater) return opts.formater(valeur);
  return formatValeur(valeur, opts.format ?? "montant", opts.devise);
}

/** Nom du pays en français depuis son code ISO-2 (« FR » → « France »). */
export function nomDuPays(codeIso2: string): string {
  try {
    const noms = new Intl.DisplayNames(["fr"], { type: "region" });
    return noms.of(codeIso2.toUpperCase()) ?? codeIso2;
  } catch {
    return codeIso2;
  }
}
